import HomeIcon from '@mui/icons-material/Home';
import SendIcon from '@mui/icons-material/Send';
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React from "react";
import { useTranslation } from "react-i18next";
import { i18_SIGNUP } from "../../i18nReferences";

const SUPPORT_EMAIL_ADDRESS = 'support@kisscal.freshdesk.com';

/**
 * Warning view for users who already have an existing account
 * Displays a warning message and a support ticket form
 * 
 * @param {Object} props
 * @param {Object} props.supportForm - React Hook Form instance for the support ticket
 * @param {Function} props.onSubmit - Callback when form is submitted
 * @param {Function} props.onNavigateHome - Callback when home button is clicked
 * @param {boolean} props.isSubjectDisabled - Whether the subject field should be disabled
 * @param {boolean} props.isPending - Whether the support ticket is being submitted
 * @param {boolean} props.isError - Whether there was an error submitting the ticket
 */
const SignupWarningView = ({ 
    supportForm, 
    onSubmit, 
    onNavigateHome,
    isSubjectDisabled,
    isPending,
    isError
}) => {
    const { t } = useTranslation([i18_SIGNUP]);
    const isSendDisabled = isPending || !supportForm.formState.isValid;

    return (
        (<Container maxWidth="md">
            <Box sx={{ mt: 4 }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    mb: 2
                }}>
                    <Typography
                        variant="h4"
                        sx={{
                            color: "warning.main",
                            flex: 1
                        }}>
                        {t('WARNING.TITLE')}
                    </Typography>
                    <Tooltip title={t('WARNING.GO_HOME_BUTTON')}>
                        <IconButton 
                            onClick={onNavigateHome}
                            color="primary"
                            size="large"
                            sx={{ ml: 2 }}
                        >
                            <HomeIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    {t('WARNING.MESSAGE')}
                </Alert>

                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                    {t('WARNING.SUPPORT_SECTION_TITLE')}
                </Typography>

                <form onSubmit={supportForm.handleSubmit(onSubmit)}>
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <Alert severity="info">
                                {t('WARNING.SUPPORT_INFO', { email: SUPPORT_EMAIL_ADDRESS })}
                            </Alert>
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                {...supportForm.register('subject')}
                                label={t('WARNING.SUBJECT_LABEL')}
                                fullWidth
                                error={!!supportForm.formState.errors?.subject}
                                variant="standard"
                                required
                                disabled={isSubjectDisabled}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                {...supportForm.register('email')}
                                label={t('WARNING.EMAIL_LABEL')}
                                fullWidth
                                error={!!supportForm.formState.errors?.email}
                                variant="standard"
                                required
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                {...supportForm.register('phone')}
                                label={t('WARNING.PHONE_LABEL')}
                                fullWidth
                                error={!!supportForm.formState.errors?.phone}
                                variant="standard"
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                {...supportForm.register('description')}
                                label={t('WARNING.DESCRIPTION_LABEL')}
                                fullWidth
                                multiline
                                rows={6}
                                error={!!supportForm.formState.errors?.description}
                                variant="standard"
                                required
                            />
                        </Grid>
                        {isPending && (
                            <Grid size={12}>
                                <Alert>{t('WARNING.SENDING')}</Alert>
                            </Grid>
                        )}
                        {isError && (
                            <Grid size={12}>
                                <Alert severity="error">
                                    {t('WARNING.ERROR_MESSAGE', { email: SUPPORT_EMAIL_ADDRESS })}
                                </Alert>
                            </Grid>
                        )}
                        <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                type="submit"
                                disabled={isSendDisabled}
                                variant="contained"
                                startIcon={<SendIcon />}
                            >
                                {t('WARNING.SEND_BUTTON')}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </Container>)
    );
};

export default SignupWarningView;

